import React, { useMemo, useId, useCallback } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  FormHelperText,
  Box,
  Typography,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/material';
import { PackageOption, Package, getPackageRule } from './packageSelect.helper';

// ---------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------

export interface PackageSelectProps {
  /** Available package options */
  options: PackageOption[];
  /** Currently selected package ID */
  value: string;
  /** Callback when selection changes */
  onChange: (value: string, selectedPackage?: Package) => void;
  /** Error message */
  error?: string;
  /** Whether to show validation errors */
  showErrors?: boolean;
  /** Optional name for form integrations */
  name?: string;
  /** Placeholder text shown when no value selected */
  placeholder?: string;
  /** Disable the select */
  disabled?: boolean;
  /** Override the visible label */
  label?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Show package details in options */
  showDetails?: boolean;
}

// ---------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------

const PACKAGE_SELECT_LABEL_DEFAULT = 'Package Selection';
const PACKAGE_SELECT_PLACEHOLDER_DEFAULT = 'Select Package';

// ---------------------------------------------------------------------
// Helper Components
// ---------------------------------------------------------------------

interface PackageOptionContentProps {
  pkg: Package;
  showDetails?: boolean;
}

const PackageOptionContent: React.FC<PackageOptionContentProps> = ({ 
  pkg, 
  showDetails = false 
}) => {
  const concessionRule = getPackageRule(pkg, 'CONCESSION');
  // patient_name comes from PackageDetails when cast as Package
  const patientName = (pkg as Package & { patient_name?: string }).patient_name;

  if (!showDetails) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
        <Typography variant="body2" fontWeight={500}>
          {pkg.package_name}
        </Typography>
        {(patientName || pkg.code) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
            {patientName && (
              <Typography variant="caption" color="text.secondary">
                {patientName}
              </Typography>
            )}
            {pkg.code && (
              <Chip
                label={pkg.code}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.7rem',
                  bgcolor: '#EBF0FA',
                  color: '#3B5998',
                  fontWeight: 500,
                }}
              />
            )}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', py: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" fontWeight={500}>
          {pkg.package_name}
        </Typography>
        <Chip 
          label={pkg.code} 
          size="small" 
          variant="outlined"
          sx={{ height: 20, fontSize: '0.7rem' }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          ₹{parseFloat(pkg.package_amount).toLocaleString()}
        </Typography>
        {concessionRule && (
          <Typography variant="caption" color="success.main">
            {concessionRule.min_percent}-{concessionRule.max_percent}% concession
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------

const PackageSelect: React.FC<PackageSelectProps> = ({
  options,
  value,
  onChange,
  error,
  showErrors = false,
  name,
  placeholder = PACKAGE_SELECT_PLACEHOLDER_DEFAULT,
  disabled = false,
  label = PACKAGE_SELECT_LABEL_DEFAULT,
  isLoading = false,
  showDetails = false,
}) => {
  const isError = Boolean(showErrors && error);
  const selectId = useId();
  const labelId = `${selectId}-label`;
  const helperTextId = `${selectId}-helper`;

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedValue = event.target.value;
      const selectedOption = options.find((opt) => opt.value === selectedValue);
      onChange(selectedValue, selectedOption?.package);
    },
    [options, onChange]
  );

  const renderedOptions = useMemo(
    () =>
      options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          <PackageOptionContent pkg={opt.package} showDetails={showDetails} />
        </MenuItem>
      )),
    [options, showDetails]
  );

  // Get selected package details for display
  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value),
    [options, value]
  );

  return (
    <FormControl
      fullWidth
      size="small"
      error={isError}
      disabled={disabled || isLoading}
      aria-describedby={isError ? helperTextId : undefined}
    >
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        id={selectId}
        name={name}
        label={label}
        value={value}
        onChange={handleChange}
        aria-invalid={isError || undefined}
        endAdornment={
          isLoading ? (
            <CircularProgress size={20} sx={{ mr: 3 }} />
          ) : undefined
        }
        renderValue={(selected) => {
          if (!selected) {
            return <Typography color="text.secondary">{placeholder}</Typography>;
          }
          if (selectedOption) {
            const fullName = selectedOption.package.package_name;
            return (
              <Tooltip 
                title={fullName}
                placement="top"
                arrow
              >
                <span>{fullName}</span>
              </Tooltip>
            );
          }
          return selected;
        }}
      >
        {placeholder && (
          <MenuItem value="">
            {placeholder}
          </MenuItem>
        )}
        {renderedOptions}
      </Select>
      {isError && (
        <FormHelperText id={helperTextId}>{error}</FormHelperText>
      )}
      {/* Show selected package summary */}
      {selectedOption && !isError && (
        <FormHelperText sx={{ color: 'text.secondary', mt: 0.5 }}>
          {selectedOption.package.short_description} • ₹
          {parseFloat(selectedOption.package.package_amount).toLocaleString()}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default PackageSelect;
